import type { ScoutRecord, ScoutTriggerConfig } from "./services/scouts";
import { getSystemScoutTimezone } from "./scout-timezone";

export interface UpcomingScout {
  scout: ScoutRecord;
  trigger: ScoutTriggerConfig;
  nextAt: Date;
}

export function buildUpcomingScouts(scouts: ScoutRecord[]): UpcomingScout[] {
  const now = new Date();
  return scouts
    .filter((scout) => scout.status !== "disabled")
    .flatMap((scout) =>
      scout.triggerConfig
        .filter((trigger) => trigger.type === "schedule" && trigger.schedule)
        .map((trigger) => {
          const nextAt = nextOccurrence(trigger, now);
          return nextAt ? { scout, trigger, nextAt } : null;
        })
        .filter((item): item is UpcomingScout => Boolean(item)),
    )
    .sort((a, b) => a.nextAt.getTime() - b.nextAt.getTime())
    .slice(0, 12);
}

export function scheduleId(trigger: ScoutTriggerConfig): string {
  const raw = trigger.cursor?.scheduleId;
  return typeof raw === "string" ? raw : (trigger.schedule ?? "schedule");
}

export function formatNextScoutTime(date: Date): string {
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function nextOccurrence(trigger: ScoutTriggerConfig, now: Date): Date | null {
  const end = now.getTime() + 24 * 60 * 60_000;
  const cursor = new Date(now.getTime() + 60_000);
  cursor.setSeconds(0, 0);
  while (cursor.getTime() <= end) {
    if (
      cronMatches(
        trigger.schedule ?? "",
        cursor,
        trigger.timezone ?? getSystemScoutTimezone(),
      )
    ) {
      return new Date(cursor);
    }
    cursor.setMinutes(cursor.getMinutes() + 1);
  }
  return null;
}

function cronMatches(cronExpr: string, now: Date, timezone: string): boolean {
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  const p = zonedParts(now, timezone);
  return (
    fieldMatches(parts[0], p.minute, 0, 59) &&
    fieldMatches(parts[1], p.hour, 0, 23) &&
    fieldMatches(parts[2], p.day, 1, 31) &&
    fieldMatches(parts[3], p.month, 1, 12) &&
    fieldMatches(parts[4], p.weekday, 0, 6)
  );
}

function fieldMatches(field: string, value: number, min: number, max: number) {
  if (field === "*") return true;
  return field.split(",").some((part) => partMatches(part, value, min, max));
}

function partMatches(part: string, value: number, min: number, max: number) {
  if (part.startsWith("*/")) return value % Number(part.slice(2)) === 0;
  if (part.includes("-")) {
    const [start, end] = part.split("-").map(Number);
    return value >= start && value <= end;
  }
  const parsed = Number(part);
  return parsed >= min && parsed <= max && value === parsed;
}

function zonedParts(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    minute: "numeric",
    hour: "numeric",
    day: "numeric",
    month: "numeric",
    weekday: "short",
    hour12: false,
  }).formatToParts(date);
  const read = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  const weekdayText = parts.find((part) => part.type === "weekday")?.value;
  return {
    minute: read("minute"),
    hour: read("hour"),
    day: read("day"),
    month: read("month"),
    weekday: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(
      weekdayText ?? "",
    ),
  };
}
